/**
 * web/src/lib/buildWatch.ts — the cache buster that does not need a refresh.
 *
 * Founder, 2026-10-17: "need a cache buster that doesn't rely on site
 * refresh". What existed was passive in both directions — UpdateToast only
 * notices a newer `__BUILD_ID__` on the NEXT navigation, and Settings → Build
 * offered a manual `location.assign('/?cb=…')`. Either way the reader had to
 * reload the page themselves to get the deploy they had not yet seen.
 *
 * So: while the app is open, re-read index.html on a timer (and whenever the
 * tab comes back or the network returns), compare the entry module's `src`
 * with the one this page booted from, and when they differ a build landed.
 *
 *   - always offer, never take: a new build emits `pear:update-available` and
 *     UpdateToast offers the reload as a button. It used to reload outright
 *     when the Thread draft was empty, which was wrong twice over (pre-deploy
 *     review, 2026-10-17): the Thread draft is not the only thing in flight —
 *     a half-composed kudos, a gift form, a sign-up half filled in, an upload
 *     on its way — and hasDraft() could see none of it. Until every dirty form
 *     and in-flight action can declare itself, the page is never reloaded out
 *     from under anybody.
 *
 * The check is `cache: 'no-store'` plus a `?cb=` stamp: the whole point is
 * that neither the HTTP cache nor the service worker's shell gets to answer
 * this one. Detection keys off the entry script's src rather than BUILD_ID
 * because the src is what the browser actually loaded — a new hash IS a new
 * bundle, with no second source of truth.
 *
 * Not gated on https/isLocal the way the service worker registration is: the
 * check is harmless against the dev server (the entry src never changes
 * there) and gating it would make the behaviour untestable off-prod.
 */

/** How often to re-read index.html. */
const POLL_MS = 60_000;

/** Name of the event fired when a build lands — UpdateToast turns it into a
 *  Reload button. The ONLY outcome of a detected build (review 4): the page is
 *  never reloaded by the watcher itself. */
export const UPDATE_EVENT = 'pear:update-available';

/** The entry module this page actually booted from, e.g. /assets/index-D5F5tRyW.js.
 *
 *  index.html carries more than one `type="module"` in dev (`/@vite/client`
 *  comes first, `./src/main.tsx` is the app), so taking the FIRST one would
 *  compare the dev client against itself and never the bundle. Prefer the
 *  hashed asset when there is one — that is the thing a deploy changes — and
 *  fall back to the LAST module script, which is the app entry in dev. */
function entryFrom(doc: Document | string): string {
  const html = typeof doc === 'string' ? doc : doc.documentElement.outerHTML;
  const srcs = [...html.matchAll(/<script[^>]*type="module"[^>]*\ssrc="([^"]+)"/gi)]
    .map((m) => m[1])
    .filter(Boolean);
  if (srcs.length === 0) return '';
  const hashed = srcs.find((s) => s.includes('/assets/')) ?? srcs[srcs.length - 1];
  try {
    return new URL(hashed, location.origin).href;
  } catch {
    return '';
  }
}

/** The entry module this page booted from, e.g. /assets/index-D5F5tRyW.js. */
function runningEntry(): string {
  return entryFrom(document);
}

/** The entry module a freshly fetched index.html points at. */
function entryIn(html: string): string {
  return entryFrom(html);
}

let started = false;

/** Re-read index.html once; emits UPDATE_EVENT when a different build answers. */
export async function checkForUpdate(): Promise<void> {
  const before = runningEntry();
  if (!before) return;

  let html = '';
  try {
    const res = await fetch(`/?cb=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    html = await res.text();
  } catch {
    // Offline / transient: a cache buster that nags on a flaky train ride is
    // worse than one that waits for the next tick.
    return;
  }

  const next = entryIn(html);
  if (!next || next === before) return;

  // Offer, never take: the watcher has no way to know what else is in flight
  // (a kudos being composed, a form half filled, an upload on its way), so the
  // reader decides when to reload.
  window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: next }));
}

/**
 * Start watching. Safe to call twice — the second call is a no-op.
 *
 * Polls on a timer, on return to the tab (a backgrounded PWA is where a
 * deploy most often lands unnoticed) and when the network comes back.
 */
export function startBuildWatch(): void {
  if (started) return;
  started = true;

  const tick = () => { void checkForUpdate(); };
  setInterval(tick, POLL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });
  window.addEventListener('online', tick);
}
